import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faBuilding, faDatabase, faSparkles } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";

interface TrackerEmptyStateProps {
	/** Opens the list's add-source flow (file import or another source). */
	onAddSource: () => void;
	/** Opens the company search to add a single company; absent when the list has no tracker yet. */
	onAddCompany?: () => void;
}

/**
 * Empty state for the list-detail "Tracked Profiles" tab (PREC-3614), shown when a Profile Tracker
 * list has no companies yet. Mirrors the lead-list designed empty state visually, but suggests
 * adding companies to track rather than reviewing leads. "Add company" is the primary action and
 * the file import secondary (PREC-3240): a single company could not be added from here at all.
 */
export const TrackerEmptyState: FunctionComponent<TrackerEmptyStateProps> = ({
	onAddSource,
	onAddCompany,
}) => {
	const { t } = useTranslation();
	const te = (key: string) => t(`profileTracker.emptyState.${key}`);

	return (
		<div className="flex h-[496px] items-center justify-center px-4">
			<div className="mx-auto flex max-w-md flex-col items-center rounded-2xl border border-border-light bg-white px-11 py-10 text-center shadow-sm">
				<div className="mb-5 flex h-[72px] w-[72px] items-center justify-center rounded-[18px] bg-light-blue">
					<Icon className="h-8 w-8 text-brand-light" icon={faSparkles} />
				</div>

				<h2 className="mb-2.5 text-lg font-bold text-text">{te("title")}</h2>
				<p className="mb-6 text-sm leading-6 text-text-meta">{te("description")}</p>

				<div className="flex flex-wrap items-center justify-center gap-2">
					{onAddCompany && (
						<Button
							color={ButtonColor.ACTION}
							image={<Icon className="mr-2" icon={faBuilding} />}
							title={te("addCompany")}
							onClick={onAddCompany}
						/>
					)}
					<Button
						color={onAddCompany ? ButtonColor.ACTION_SECONDARY : ButtonColor.ACTION}
						image={<Icon className="mr-2" icon={faDatabase} />}
						title={te("addSource")}
						onClick={onAddSource}
					/>
				</div>
			</div>
		</div>
	);
};
